import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LayoutGrid } from "lucide-react";
import { CategoryIcon } from "@/components/commerce/CategoryIcon";
import { useCategoryTree } from "@/features/catalog/api";
import { cn } from "@/lib/cn";

/** Desktop category mega menu (hover or click; Esc closes; fully keyboard reachable). */
export function MegaMenu({ label = "Categories", align = "left" }: { label?: string; align?: "left" | "right" } = {}) {
  const { data } = useCategoryTree();
  const [open, setOpen] = useState(false);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  const hovering = useRef(false);
  const active = data?.find((c) => c.slug === activeSlug) ?? data?.[0];

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative hidden lg:block"
      onMouseEnter={() => {
        window.clearTimeout(closeTimer.current);
        hovering.current = true;
        setOpen(true);
      }}
      onMouseLeave={() => {
        hovering.current = false;
        closeTimer.current = window.setTimeout(() => setOpen(false), 150);
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => (hovering.current ? true : !v))}
        className="inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm text-ink-soft hover:bg-fg/5 hover:text-ink"
      >
        {label === "Categories" && <LayoutGrid className="size-4" aria-hidden />} {label}{" "}
        <ChevronDown className={cn("size-3.5 transition", open && "rotate-180")} aria-hidden />
      </button>
      {open && data && (
        <div
          className={cn(
            "absolute top-full z-50 mt-2 flex w-[760px]",
            align === "right" ? "right-0" : "left-0",
            "animate-pop-in overflow-hidden rounded-card border border-line bg-elevated shadow-glow-3 dark:border-[var(--border-glow)]",
          )}
        >
          <ul className="w-60 shrink-0 border-r border-line p-2">
            {data.map((root) => (
              <li key={root.slug}>
                <Link
                  to={`/c/${root.slug}`}
                  onMouseEnter={() => setActiveSlug(root.slug)}
                  onFocus={() => setActiveSlug(root.slug)}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2 text-sm",
                    active?.slug === root.slug ? "bg-primary/15 text-ink" : "text-ink-soft hover:text-ink",
                  )}
                >
                  <CategoryIcon name={root.icon} className="size-4 text-cyan" />
                  {root.name}
                </Link>
              </li>
            ))}
          </ul>
          {active && (
            <div className="grid flex-1 auto-rows-min grid-cols-2 gap-x-6 gap-y-5 p-5">
              {active.children.map((branch) => (
                <div key={branch.slug}>
                  <Link
                    to={`/c/${branch.slug}`}
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold text-ink hover:text-[var(--primary-text)]"
                  >
                    {branch.name}
                  </Link>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {branch.children.map((leaf) => (
                      <li key={leaf.slug}>
                        <Link to={`/c/${leaf.slug}`} onClick={() => setOpen(false)} className="text-sm text-ink-muted hover:text-ink">
                          {leaf.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
